import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserCheck, Search, ArrowLeft, RotateCcw, Dumbbell, X, ChevronRight, FileText, Clock, Activity, CheckCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = "#C9A84C";

// ── SPORTER RIJ ────────────────────────────────────────────────────────────────
function SporterRij({ sporter, onSelect }) {
  const initials = (sporter.naam || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["#C9A84C", "#4C9A84", "#844CC9", "#C94C4C", "#4C84C9"];
  const color = colors[(sporter.naam || "").charCodeAt(0) % colors.length];
  return (
    <button
      data-testid={`pt-sporter-${sporter.id}`}
      onClick={() => onSelect(sporter)}
      className="w-full flex items-center gap-4 p-4 rounded-2xl border border-white/8 bg-white/3 hover:bg-white/6 hover:border-white/15 transition-all text-left group">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-base font-black text-black shrink-0"
        style={{ background: color }}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm">{sporter.naam}</p>
        <p className="text-zinc-600 text-xs truncate">{sporter.email}</p>
      </div>
      <ChevronRight size={16} className="text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0" />
    </button>
  );
}

// ── ACTIEVE PT SESSIE ──────────────────────────────────────────────────────────
function ActievePTSessie({ sessieData, onEindigen }) {
  const { token, sporter, recente_sessies } = sessieData;
  const navigate = useNavigate();
  const [notitie, setNotitie] = useState("");
  const [notitieOpgeslagen, setNotitieOpgeslagen] = useState(false);
  const [kioskUser, setKioskUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  // Haal sporter profiel op met kiosk token
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setKioskUser({ ...d, token }); setLoadingUser(false); })
      .catch(() => setLoadingUser(false));
  }, [token]);

  const slaNotitieOp = useCallback(async () => {
    if (!notitie.trim()) return;
    try {
      await fetch(`${API}/api/pt/sessie-notitie/${sporter.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note: notitie }),
      });
      setNotitieOpgeslagen(true);
      setNotitie("");
      setTimeout(() => setNotitieOpgeslagen(false), 3000);
    } catch (e) {
      console.error("Notitie opslaan mislukt:", e);
    }
  }, [notitie, token, sporter]);

  return (
    <div className="min-h-screen bg-[#080808]">
      {/* Floating header met sporter info + sessie beëindigen */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {/* Sporter badge */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center text-xs font-black text-black shrink-0"
              style={{ background: GOLD }}>
              {(sporter.naam || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm truncate">{sporter.naam}</p>
              <p className="text-zinc-600 text-xs">Personal Training Sessie</p>
            </div>
          </div>
          {/* Sessie beëindigen */}
          <button
            data-testid="sessie-beeindigen-btn"
            onClick={onEindigen}
            className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm border border-white/10 hover:border-white/20 transition-all"
            style={{ background: "#111", color: "#e5e5e5" }}>
            <X size={14} />
            Beëindigen
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-20 pb-10">
        {/* PT Notitie sectie */}
        <div className="mb-6 bg-[#111] border border-white/8 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={14} style={{ color: GOLD }} />
            <p className="text-white font-bold text-sm">Sessienotitie</p>
          </div>
          <textarea
            placeholder="Voeg een notitie toe over deze trainingssessie..."
            value={notitie}
            onChange={e => setNotitie(e.target.value)}
            rows={3}
            className="w-full bg-white/3 border border-white/8 rounded-xl p-3 text-white text-sm placeholder:text-zinc-700 resize-none focus:outline-none focus:border-white/20 transition-colors"
          />
          <div className="flex items-center justify-between mt-2">
            {notitieOpgeslagen && (
              <span className="text-xs font-semibold" style={{ color: GOLD }}>Notitie opgeslagen!</span>
            )}
            <div className="ml-auto">
              <button
                onClick={slaNotitieOp}
                disabled={!notitie.trim()}
                className="px-4 py-2 rounded-xl text-xs font-bold text-black disabled:opacity-40 transition-all"
                style={{ background: GOLD }}>
                Opslaan
              </button>
            </div>
          </div>
        </div>

        {/* Recente sessies */}
        {recente_sessies && recente_sessies.length > 0 && (
          <div className="mb-6 bg-[#111] border border-white/8 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={14} style={{ color: GOLD }} />
              <p className="text-white font-bold text-sm">Recente PT sessies</p>
            </div>
            <div className="space-y-2">
              {recente_sessies.slice(0, 3).map((s, i) => (
                <div key={i} className="bg-white/3 rounded-xl p-3 text-xs">
                  <p className="text-zinc-400">{new Date(s.gestart_op).toLocaleDateString("nl-NL", { day: "numeric", month: "short" })}</p>
                  {s.notitie && <p className="text-white mt-1">{s.notitie}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Schema's van de sporter */}
        {!loadingUser && kioskUser && (
          <div className="bg-[#111] border border-white/8 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Activity size={14} style={{ color: GOLD }} />
              <p className="text-white font-bold text-sm">Schema's starten</p>
            </div>
            <PTSporterSchemas kioskUser={kioskUser} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── PT SPORTER SCHEMAS ──────────────────────────────────────────────────────────
function PTSporterSchemas({ kioskUser }) {
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snel, setSnel] = useState(null); // {schemaId, dagIdx, dagNaam, schemaKey}
  const [snelOpgeslagen, setSnelOpgeslagen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!kioskUser?.token) return;
    fetch(`${API}/api/sporter/schemas`, {
      headers: { Authorization: `Bearer ${kioskUser.token}` },
    })
      .then(r => r.json())
      .then(d => { setSchemas(d.schemas || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [kioskUser]);

  const startTraining = (schemaId, dagIdx) => {
    // Sla kiosk token op zodat WorkoutCustomLogger het gebruikt
    sessionStorage.setItem("kiosk_token", kioskUser.token);
    sessionStorage.setItem("kiosk_user_id", kioskUser._id || kioskUser.id || "");
    navigate(`/workout-custom/${schemaId}/${dagIdx}`);
  };

  const snelVastleggen = async (s, dagIdx, dag) => {
    setSaving(true);
    const sessionId = `custom-${s.id}-dag${dagIdx}`;
    try {
      await fetch(`${API}/api/workout/${s.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${kioskUser.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          session_naam: dag.dag_naam || dag.naam || `Dag ${dagIdx + 1}`,
          training_naam: s.naam,
          duration_seconds: 0,
          exercises: [],
        }),
      });
      setSnelOpgeslagen(true);
      setSnel(null);
      setTimeout(() => setSnelOpgeslagen(false), 3000);
    } catch {}
    setSaving(false);
  };

  if (loading) return <div className="h-16 bg-white/3 rounded-xl animate-pulse" />;
  if (schemas.length === 0) return <p className="text-zinc-600 text-sm">Geen schema's toegewezen.</p>;

  return (
    <div className="space-y-3">
      {snelOpgeslagen && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold" style={{ background: "rgba(201,168,76,0.1)", color: GOLD }}>
          <CheckCircle size={13} /> Training vastgelegd in voortgang!
        </div>
      )}
      {schemas.map(s => (
        <div key={s.id} className="bg-white/3 border border-white/8 rounded-xl p-3">
          <p className="text-white font-semibold text-sm">{s.naam}</p>
          <p className="text-zinc-600 text-xs mt-0.5">{s.dagen?.length || 0} trainingsdagen</p>
          {s.dagen && s.dagen.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {s.dagen.map((dag, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button
                    data-testid={`pt-start-training-${s.id}-dag-${i}`}
                    onClick={() => startTraining(s.id, i)}
                    className="flex-1 px-3 py-1.5 rounded-lg text-xs font-bold text-black transition-all active:scale-95 text-left"
                    style={{ background: GOLD }}>
                    {dag.dag_naam || dag.naam || `Dag ${i + 1}`}
                  </button>
                  <button
                    data-testid={`pt-snel-${s.id}-dag-${i}`}
                    onClick={() => snel?.schemaId === s.id && snel?.dagIdx === i ? setSnel(null) : setSnel({ schemaId: s.id, dagIdx: i, dag, s })}
                    title="Snel vastleggen"
                    className="px-2.5 py-1.5 rounded-lg text-xs text-zinc-500 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
                  >
                    Snel
                  </button>
                </div>
              ))}
              {snel?.schemaId === s.id && (
                <div className="bg-black/30 border border-white/10 rounded-xl p-3 mt-2">
                  <p className="text-zinc-400 text-xs mb-2">Training markeren als voltooid zonder oefeningen in te vullen:</p>
                  <p className="text-white text-xs font-bold mb-2">{snel.dag.dag_naam || snel.dag.naam || `Dag ${snel.dagIdx + 1}`} — {s.naam}</p>
                  <button
                    onClick={() => snelVastleggen(s, snel.dagIdx, snel.dag)}
                    disabled={saving}
                    data-testid="pt-snel-vastleggen-btn"
                    className="w-full px-3 py-2 rounded-xl text-xs font-bold text-black disabled:opacity-50 transition-all"
                    style={{ background: GOLD }}
                  >
                    {saving ? "Bezig..." : "Vastleggen"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── PT MODUS HOOFD ─────────────────────────────────────────────────────────────
export default function PTModus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sporters, setSporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [zoekterm, setZoekterm] = useState("");
  const [activeSessie, setActiveSessie] = useState(null);
  const [starting, setStarting] = useState(false);

  const heeftToegang = ["company_admin", "trainer", "admin"].includes(user?.role);

  useEffect(() => {
    if (!user?.token || !heeftToegang) return;
    fetch(`${API}/api/company/sporters`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then(r => r.json())
      .then(d => { setSporters(d.sporters || []); setLoading(false); })
      .catch(() => { setError("Sporters laden mislukt"); setLoading(false); });
  }, [user, heeftToegang]);

  const startPTSessie = useCallback(async (sporter) => {
    setStarting(true);
    try {
      const r = await fetch(`${API}/api/pt/start-session/${sporter.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${user.token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ note: "" }),
      });
      if (!r.ok) throw new Error("Sessie starten mislukt");
      const data = await r.json();
      setActiveSessie(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  }, [user]);

  const eindigSessie = useCallback(() => {
    setActiveSessie(null);
  }, []);

  const gefilterd = sporters.filter(s =>
    !zoekterm || s.naam.toLowerCase().includes(zoekterm.toLowerCase()) || s.email.toLowerCase().includes(zoekterm.toLowerCase())
  );

  if (!heeftToegang) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-white font-bold mb-2">Geen toegang</p>
          <p className="text-zinc-500 text-sm">PT Modus is alleen voor trainers.</p>
        </div>
      </div>
    );
  }

  if (activeSessie) {
    return <ActievePTSessie sessieData={activeSessie} onEindigen={eindigSessie} />;
  }

  return (
    <div className="min-h-screen bg-[#080808] pb-10">
      <div className="sticky top-0 z-10 bg-[#080808]/95 backdrop-blur-md border-b border-white/5 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            <ArrowLeft size={16} className="text-zinc-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}20` }}>
                <UserCheck size={14} style={{ color: GOLD }} />
              </div>
              <h1 className="text-white font-black text-lg">Personal Training</h1>
            </div>
            <p className="text-zinc-600 text-xs">Selecteer een sporter voor 1-op-1 sessie</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6">
        <div className="relative mb-6">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="pt-zoekbalk"
            type="text"
            placeholder="Zoek sporter..."
            value={zoekterm}
            onChange={e => setZoekterm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/8 bg-white/3 text-white text-sm placeholder:text-zinc-700 focus:outline-none focus:border-white/20"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-white/3 animate-pulse" />)}
          </div>
        ) : gefilterd.length === 0 ? (
          <div className="text-center py-16">
            <Dumbbell size={40} className="text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400">Geen sporters gevonden</p>
          </div>
        ) : (
          <div className="space-y-3">
            {gefilterd.map(s => (
              <SporterRij key={s.id} sporter={s} onSelect={startPTSessie} />
            ))}
          </div>
        )}

        {starting && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
              <div className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin mx-auto mb-4"
                style={{ borderColor: GOLD, borderTopColor: "transparent" }} />
              <p className="text-white font-bold">PT Sessie starten...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
